import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Avatar,
  Chip,
  IconButton,
  Alert,
  Breadcrumbs,
  Link,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  InputAdornment,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add,
  Business,
  Person,
  Email,
  Phone,
  LocationOn,
  Edit,
  Visibility,
  Search,
  Home,
  Assignment,
  TrendingUp,
  CheckCircle,
  Visibility as VisibilityIcon,
  VisibilityOff,
} from '@mui/icons-material';
import { useAuth, UserRole } from '../contexts/AuthContext';
import { clientsService, Client, ClientType, CreateClientDto } from '../services/clientsService';

const Clients: React.FC = () => {
  const { user, hasRole } = useAuth();
  
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [totalClients, setTotalClients] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  const [newClient, setNewClient] = useState<CreateClientDto>({
    company_name: '',
    client_type: ClientType.COMPAGNIE_MINIERE,
    email: '',
    phone: '',
    address: '',
    country: 'Maroc',
  });
  
  const [editClient, setEditClient] = useState<Client | null>(null);

  useEffect(() => {
    loadClients();
  }, [currentPage, searchTerm, filterType]);

  const loadClients = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await clientsService.getClients(
        currentPage, 
        itemsPerPage,
        searchTerm || undefined,
        filterType || undefined
      );
      setClients(response.clients);
      setTotalClients(response.total);
      setTotalPages(Math.ceil(response.total / itemsPerPage));
    } catch (error) {
      console.error('Error loading clients:', error);
      setError('Erreur lors du chargement des clients');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async () => {
    try {
      setError(null);
      await clientsService.createClient(newClient);
      setShowAddDialog(false);
      setNewClient({
        company_name: '',
        client_type: ClientType.COMPAGNIE_MINIERE,
        email: '',
        phone: '',
        address: '',
        country: 'Maroc',
      });
      await loadClients(); // Reload the list
    } catch (error) {
      console.error('Error creating client:', error);
      setError('Erreur lors de la création du client');
    }
  };

  const handleViewClient = (client: Client) => {
    setSelectedClient(client);
    setShowViewDialog(true);
  };

  const handleEditClient = (client: Client) => {
    setEditClient({ ...client });
    setShowEditDialog(true);
    setShowEditPassword(false);
  };

  const handleUpdateClient = async () => {
    if (!editClient) return;
    
    try {
      setError(null);
      
      // Extract portal_password from editClient and create a copy without it
      const { portal_password, ...clientDataWithoutPassword } = editClient;
      
      // Update client data (excluding password)
      await clientsService.updateClient(editClient.id, clientDataWithoutPassword);
      
      // Update portal password separately if provided
      if (portal_password && portal_password.trim()) {
        await clientsService.updateClientPortalPassword(editClient.id, portal_password);
      }
      
      setShowEditDialog(false);
      setEditClient(null);
      await loadClients();
    } catch (error) {
      console.error('Error updating client:', error);
      setError('Erreur lors de la mise à jour du client');
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const getClientTypeLabel = (type: string): string => {
    switch (type) {
      case 'COMPAGNIE_MINIERE': return 'Compagnie Minière';
      case 'JUNIOR_MINING': return 'Junior Minière';
      case 'CONSULTANT': return 'Consultant';
      case 'LABORATOIRE': return 'Laboratoire';
      case 'GOUVERNEMENT': return 'Gouvernement';
      case 'UNIVERSITE': return 'Université';
      default: return type;
    }
  };

  const getClientTypeColor = (type: string) => {
    switch (type) {
      case 'COMPAGNIE_MINIERE': return 'primary';
      case 'JUNIOR_MINING': return 'secondary';
      case 'CONSULTANT': return 'info';
      case 'LABORATOIRE': return 'success';
      case 'GOUVERNEMENT': return 'warning';
      case 'UNIVERSITE': return 'error';
      default: return 'default';
    }
  };

  const clientStats = {
    total: totalClients || 0,
    active: clients.filter(c => c.is_active).length || 0,
    totalSamples: clients.reduce((sum, c) => sum + (c.samples_count || 0), 0) || 0,
    avgSamplesPerClient: clients.length > 0 ? Math.round(clients.reduce((sum, c) => sum + (c.samples_count || 0), 0) / clients.length) || 0 : 0
  };

  return (
    <Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link color="inherit" href="/dashboard" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Home fontSize="small" />
          Tableau de bord
        </Link>
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Business fontSize="small" />
          Clients
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Gestion des Clients
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gérez vos clients et leurs informations de contact
          </Typography>
        </Box>
        {(() => {
          const canAddClient = hasRole([UserRole.ADMIN, UserRole.LABORATORY_MANAGER, UserRole.SAMPLE_RECEIVER]);
          console.log('🔍 Client Add Button - User Role:', user?.role, 'Can Add:', canAddClient);
          return canAddClient;
        })() && (
          <Button variant="contained" startIcon={<Add />} onClick={() => setShowAddDialog(true)}>
            Nouveau Client
          </Button>
        )}
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Total Clients
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {clientStats.total}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                  <Business sx={{ fontSize: 28 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Clients Actifs
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {clientStats.active}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'success.main', width: 56, height: 56 }}>
                  <CheckCircle sx={{ fontSize: 28 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Total Échantillons
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {clientStats.totalSamples}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'info.main', width: 56, height: 56 }}>
                  <Assignment sx={{ fontSize: 28 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Moyenne/Client
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {clientStats.avgSamplesPerClient}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'warning.main', width: 56, height: 56 }}>
                  <TrendingUp sx={{ fontSize: 28 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Rechercher par nom, contact ou email..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Type de Client</InputLabel>
                <Select
                  value={filterType}
                  onChange={(e) => {
                    setFilterType(e.target.value);
                    setCurrentPage(1);
                  }}
                  label="Type de Client"
                >
                  <MenuItem value="">Tous les types</MenuItem>
                  <MenuItem value="COMPAGNIE_MINIERE">Compagnie Minière</MenuItem>
                  <MenuItem value="JUNIOR_MINING">Junior Minière</MenuItem>
                  <MenuItem value="CONSULTANT">Consultant</MenuItem>
                  <MenuItem value="LABORATOIRE">Laboratoire</MenuItem>
                  <MenuItem value="GOUVERNEMENT">Gouvernement</MenuItem>
                  <MenuItem value="UNIVERSITE">Université</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Typography variant="body2" color="text.secondary">
                {totalClients} client(s) trouvé(s)
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Clients List */}
      <Grid container spacing={3}>
        {clients.map((client) => (
          <Grid item xs={12} md={6} lg={4} key={client.id}>
            <Card sx={{ height: '100%', position: 'relative' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                    {client.company_name.charAt(0)}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight="bold" noWrap>
                      {client.company_name}
                    </Typography>
                    <Chip 
                      label={getClientTypeLabel(client.client_type)} 
                      size="small" 
                      color={getClientTypeColor(client.client_type)}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton size="small" onClick={() => handleViewClient(client)}>
                      <Visibility fontSize="small" />
                    </IconButton>
                    {hasRole([UserRole.ADMIN, UserRole.LABORATORY_MANAGER]) && (
                      <IconButton size="small" onClick={() => handleEditClient(client)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Box>

                <List dense>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Person fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={client.contact_person || 'N/A'}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Email fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={client.email}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Phone fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={client.phone}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <LocationOn fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={`${client.city}, ${client.country}`}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                </List>

                <Divider sx={{ my: 1 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Échantillons
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {client.samples_count || 0}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" color="text.secondary">
                      Dernier échantillon
                    </Typography>
                    <Typography variant="body2">
                      {client.last_sample_date ? new Date(client.last_sample_date).toLocaleDateString('fr-FR') : 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {clients.length === 0 && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Business sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Aucun client trouvé
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchTerm || filterType ? 'Essayez de modifier vos critères de recherche' : 'Commencez par ajouter votre premier client'}
          </Typography>
        </Paper>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Add Client Dialog */}
      <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Nouveau Client</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nom de l'entreprise"
                value={newClient.company_name}
                onChange={(e) => setNewClient({ ...newClient, company_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Type de Client</InputLabel>
                <Select
                  value={newClient.client_type}
                  onChange={(e) => setNewClient({ ...newClient, client_type: e.target.value as ClientType })}
                  label="Type de Client"
                >
                  <MenuItem value={ClientType.COMPAGNIE_MINIERE}>Compagnie Minière</MenuItem>
                  <MenuItem value={ClientType.JUNIOR_MINING}>Junior Minière</MenuItem>
                  <MenuItem value={ClientType.CONSULTANT}>Consultant</MenuItem>
                  <MenuItem value={ClientType.LABORATOIRE}>Laboratoire</MenuItem>
                  <MenuItem value={ClientType.GOUVERNEMENT}>Gouvernement</MenuItem>
                  <MenuItem value={ClientType.UNIVERSITE}>Université</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Personne de contact"
                value={newClient.contact_person || ''}
                onChange={(e) => setNewClient({ ...newClient, contact_person: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={newClient.email}
                onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Téléphone"
                value={newClient.phone}
                onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Adresse"
                multiline
                rows={2}
                value={newClient.address}
                onChange={(e) => setNewClient({ ...newClient, address: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Ville"
                value={newClient.city || ''}
                onChange={(e) => setNewClient({ ...newClient, city: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Pays"
                value={newClient.country}
                onChange={(e) => setNewClient({ ...newClient, country: e.target.value })}
                required
              />
            </Grid>
            
            {/* 🚀 CLIENT PORTAL PASSWORD SECTION */}
            <Grid item xs={12}>
              <Typography variant="h6" color="primary" sx={{ mt: 2, mb: 1 }}>
                🔐 Accès Portail Client
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type={showPassword ? 'text' : 'password'}
                label="Mot de passe du portail client"
                value={newClient.portal_password || ''}
                onChange={(e) => setNewClient({ ...newClient, portal_password: e.target.value })}
                helperText="Créez un mot de passe pour que le client puisse accéder à son portail en ligne"
                placeholder="Entrez un mot de passe sécurisé"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)}>Annuler</Button>
          <Button 
            onClick={handleCreateClient} 
            variant="contained"
            disabled={!newClient.company_name || !newClient.email || !newClient.phone || !newClient.address}
          >
            Créer Client
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Client Dialog */}
      <Dialog open={showViewDialog} onClose={() => setShowViewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              {selectedClient?.company_name.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="h6">{selectedClient?.company_name}</Typography>
              <Chip 
                label={selectedClient ? getClientTypeLabel(selectedClient.client_type) : ''} 
                size="small" 
                color={selectedClient ? getClientTypeColor(selectedClient.client_type) : 'default'}
              />
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedClient && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Personne de contact
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedClient.contact_person || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Email
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedClient.email}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Téléphone
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedClient.phone}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Ville, Pays
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedClient.city}, {selectedClient.country}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Adresse
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedClient.address}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Statut
                  </Typography>
                  <Chip 
                    label={selectedClient.is_active ? 'Actif' : 'Inactif'} 
                    color={selectedClient.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Date de création
                  </Typography>
                  <Typography variant="body1">
                    {new Date(selectedClient.created_at).toLocaleDateString('fr-FR')}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Nombre d'échantillons
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {selectedClient.samples_count || 0}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Dernier échantillon
                  </Typography>
                  <Typography variant="body1">
                    {selectedClient.last_sample_date ? new Date(selectedClient.last_sample_date).toLocaleDateString('fr-FR') : 'Aucun'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowViewDialog(false)}>Fermer</Button>
          {hasRole([UserRole.ADMIN, UserRole.LABORATORY_MANAGER]) && selectedClient && (
            <Button 
              onClick={() => {
                setShowViewDialog(false);
                handleEditClient(selectedClient);
              }} 
              variant="contained"
            >
              Modifier
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit Client Dialog */}
      <Dialog open={showEditDialog} onClose={() => setShowEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Modifier Client</DialogTitle>
        <DialogContent>
          {editClient && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nom de l'entreprise"
                  value={editClient.company_name}
                  onChange={(e) => setEditClient({ ...editClient, company_name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Type de Client</InputLabel>
                  <Select
                    value={editClient.client_type}
                    onChange={(e) => setEditClient({ ...editClient, client_type: e.target.value as ClientType })}
                    label="Type de Client"
                  >
                    <MenuItem value={ClientType.COMPAGNIE_MINIERE}>Compagnie Minière</MenuItem>
                    <MenuItem value={ClientType.JUNIOR_MINING}>Junior Minière</MenuItem>
                    <MenuItem value={ClientType.CONSULTANT}>Consultant</MenuItem>
                    <MenuItem value={ClientType.LABORATOIRE}>Laboratoire</MenuItem>
                    <MenuItem value={ClientType.GOUVERNEMENT}>Gouvernement</MenuItem>
                    <MenuItem value={ClientType.UNIVERSITE}>Université</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Personne de contact"
                  value={editClient.contact_person || ''}
                  onChange={(e) => setEditClient({ ...editClient, contact_person: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={editClient.email}
                  onChange={(e) => setEditClient({ ...editClient, email: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Téléphone"
                  value={editClient.phone}
                  onChange={(e) => setEditClient({ ...editClient, phone: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Adresse"
                  multiline
                  rows={2}
                  value={editClient.address}
                  onChange={(e) => setEditClient({ ...editClient, address: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Ville"
                  value={editClient.city || ''}
                  onChange={(e) => setEditClient({ ...editClient, city: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Pays"
                  value={editClient.country}
                  onChange={(e) => setEditClient({ ...editClient, country: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editClient.is_active}
                      onChange={(e) => setEditClient({ ...editClient, is_active: e.target.checked })}
                    />
                  }
                  label="Client actif"
                />
              </Grid>
              
              {/* Password Section */}
              <Grid item xs={12}>
                <Typography variant="h6" color="primary" sx={{ mt: 2, mb: 1 }}>
                  🔐 Accès Portail Client
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type={showEditPassword ? 'text' : 'password'}
                  label="Nouveau mot de passe du portail"
                  value={editClient.portal_password || ''}
                  onChange={(e) => setEditClient({ ...editClient, portal_password: e.target.value })}
                  helperText="Laissez vide pour ne pas modifier le mot de passe existant"
                  placeholder="Entrez un nouveau mot de passe (optionnel)"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowEditPassword(!showEditPassword)}
                          edge="end"
                        >
                          {showEditPassword ? <VisibilityOff /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditDialog(false)}>Annuler</Button>
          <Button 
            onClick={handleUpdateClient} 
            variant="contained"
            disabled={!editClient?.company_name || !editClient?.email || !editClient?.phone || !editClient?.address}
          >
            Mettre à jour
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Clients; 